import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { medicalHistoryService, patientService, doctorService } from '../services/api';
import { venezuelaLocations } from '../utils/venezuelaLocations';
import EyeCanvas from '../components/EyeCanvas';
import './MedicalHistory.css';

const MedicalHistory = () => {
    const navigate = useNavigate();
    const { user, isAdmin, isDoctor } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [histories, setHistories] = useState([]);
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [currentDoctor, setCurrentDoctor] = useState(null);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // Patient search
    const [patientSearch, setPatientSearch] = useState('');
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);

    // Full patient registration
    const [showPatientForm, setShowPatientForm] = useState(false);
    const [patientFormData, setPatientFormData] = useState({
        firstName: '',
        secondName: '',
        firstLastName: '',
        secondLastName: '',
        sex: 'masculino',
        birthDate: '',
        documentType: 'cedula',
        documentPrefix: 'V',
        documentNumber: '',
        nationality: 'Venezolana',
        state: 'Distrito Capital',
        municipality: '',
        parish: '',
        city: 'Caracas',
        street: '',
        houseNumber: '',
        address: '',
        phone: '',
        email: '',
        paymentMode: 'particular'
    });
    const [availableMunicipalities, setAvailableMunicipalities] = useState([]);
    const [availableParishes, setAvailableParishes] = useState([]);

    const [formData, setFormData] = useState({
        patient: '',
        doctor: '',
        specialty: '',
        consultDate: new Date().toISOString().split('T')[0],
        consultReason: '',
        patientOccupation: '',
        referredBy: '',

        // Antecedentes Oftalmológicos
        ophthalmologicHistory: {
            previousSurgeries: '',
            ocularTrauma: '',
            previousEyeDiseases: '',
            ophthalmologicMedications: '',
            contactLensHistory: '',
            lastEyeExam: ''
        },

        // Antecedentes Médicos Generales
        medicalHistory: {
            systemicDiseases: '',
            allergies: '',
            previousSurgeriesHospitalizations: '',
            currentMedications: ''
        },

        // Antecedentes Familiares
        familyHistory: '',

        // Agudeza Visual
        visualAcuity: {
            rightEye: '',
            leftEye: ''
        },

        // Refracción Pre-Ciclo
        refractionPreCycle: {
            rightEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            },
            leftEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            }
        },

        // Cicloplejía
        cycloplegia: {
            rightEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            },
            leftEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            }
        },

        // FC (Final con Corrección)
        finalCorrection: {
            rightEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            },
            leftEye: {
                sphere: '',
                cylinder: '',
                axis: '',
                visualAcuity: ''
            }
        },

        // Adición
        addition: {
            rightEye: '',
            leftEye: ''
        },

        // Tipo de Lente
        lensType: 'Vision sencilla',

        // Altura
        height: '',

        // DIP (Distancia Inter-Pupilar)
        dip: '',

        // Filtro
        filter: 'Anti-reflejo',

        // Biomicroscopía
        biomicroscopy: {
            rightEyeImage: '',
            leftEyeImage: '',
            notes: ''
        },

        // BUT (Tiempo de Ruptura Lagrimal)
        tearBreakupTime: {
            rightEye: '',
            leftEye: ''
        },

        // Presión Intraocular
        intraocularPressure: {
            rightEye: '',
            leftEye: ''
        },

        // Fondo de Ojo
        fundoscopy: {
            rightEye: '',
            leftEye: ''
        },

        // Diagnóstico
        diagnosis: '',

        // Medicamentos Recetados
        medications: '',

        // Indicaciones y Recomendaciones
        indicationsRecommendations: '',

        // Próximo Control (default: 1 año después)
        nextControlDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
    });

    // Helper function to get doctor prefix based on gender
    const getDoctorPrefix = (doctor) => {
        if (!doctor) return 'Dr.';
        return doctor.gender === 'femenino' ? 'Dra.' : 'Dr.';
    };

    useEffect(() => {
        fetchHistories();
        fetchPatients();
        fetchDoctors();
        findCurrentDoctor();
    }, []);

    // Filter patients when search changes
    useEffect(() => {
        if (patientSearch.trim() === '') {
            setFilteredPatients([]);
            return;
        }

        const searchLower = patientSearch.toLowerCase();
        const filtered = patients.filter(patient => {
            const fullName = `${patient.firstName} ${patient.secondName || ''} ${patient.firstLastName} ${patient.secondLastName || ''}`.toLowerCase();
            const documentNumber = patient.documentNumber || '';
            return fullName.includes(searchLower) || documentNumber.includes(patientSearch);
        });

        setFilteredPatients(filtered.slice(0, 10)); // Limit to 10 results
    }, [patientSearch, patients]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.search-container')) {
                setShowPatientDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchHistories = async () => {
        try {
            setLoading(true);
            setError(''); // Clear any previous errors
            const response = await medicalHistoryService.getAll();
            setHistories(response.data || []);
        } catch (err) {
            // Only show error if it's not a 404 (no data) or authentication issue
            if (err.response?.status === 401) {
                setError('No tienes autorización para ver las historias médicas');
            } else if (err.response?.status !== 404) {
                setError('Error al cargar las historias médicas');
            }
            console.error('Error fetching histories:', err);
            setHistories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            const response = await patientService.getAll();
            setPatients(response.data || []);
        } catch (err) {
            console.error('Error fetching patients:', err);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await doctorService.getAll();
            setDoctors(response.data || []);
        } catch (err) {
            console.error('Error fetching doctors:', err);
        }
    };

    const findCurrentDoctor = async () => {
        if (user?.role === 'medico') {
            try {
                const response = await doctorService.getAll();
                const doctors = response.data || [];

                // Try to find doctor by email first
                let doctorRecord = doctors.find(d => d.email === user.email);

                // If not found by email, try to find by name
                if (!doctorRecord) {
                    doctorRecord = doctors.find(d => {
                        const doctorFullName = `${d.firstName} ${d.firstLastName}`.toLowerCase();
                        const userFullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                        return doctorFullName === userFullName;
                    });
                }

                if (doctorRecord) {
                    setCurrentDoctor(doctorRecord);
                    setFormData(prev => ({
                        ...prev,
                        doctor: doctorRecord._id,
                        specialty: doctorRecord.specialty[0] || ''
                    }));
                }
            } catch (err) {
                console.error('Error finding current doctor:', err);
            }
        }
    };

    const handlePatientSelect = async (patient) => {
        // NOTE: Removed restriction - patients can now have multiple medical histories
        // Each history represents a different consultation/visit
        try {
            setSelectedPatient(patient);
            setPatientSearch(`${patient.firstName} ${patient.firstLastName} - ${patient.documentNumber}`);
            setFormData(prev => ({
                ...prev,
                patient: patient._id
            }));
            setShowPatientDropdown(false);
        } catch (err) {
            console.error('Error checking existing history:', err);
            // If there's an error checking, allow to proceed
            setSelectedPatient(patient);
            setPatientSearch(`${patient.firstName} ${patient.firstLastName} - ${patient.documentNumber}`);
            setFormData(prev => ({
                ...prev,
                patient: patient._id
            }));
            setShowPatientDropdown(false);
        }
    };

    const handlePatientFormInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'state') {
            const municipalities = venezuelaLocations[value]?.municipalities || {};
            setAvailableMunicipalities(Object.keys(municipalities));
            setAvailableParishes([]);
            setPatientFormData(prev => ({
                ...prev,
                state: value,
                municipality: '',
                parish: ''
            }));
        } else if (name === 'municipality') {
            const parishes = venezuelaLocations[patientFormData.state]?.municipalities[value] || [];
            setAvailableParishes(parishes);
            setPatientFormData(prev => ({
                ...prev,
                municipality: value,
                parish: ''
            }));
        } else {
            setPatientFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Initialize municipalities when patient form opens
    useEffect(() => {
        if (showPatientForm && patientFormData.state) {
            const municipalities = venezuelaLocations[patientFormData.state]?.municipalities || {};
            setAvailableMunicipalities(Object.keys(municipalities));

            if (patientFormData.municipality) {
                const parishes = venezuelaLocations[patientFormData.state]?.municipalities[patientFormData.municipality] || [];
                setAvailableParishes(parishes);
            }
        }
    }, [showPatientForm, patientFormData.state, patientFormData.municipality]);

    const handlePatientFormSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await patientService.create(patientFormData);

            if (response.success) {
                alert('Paciente registrado exitosamente');

                // Refresh patients list
                await fetchPatients();

                // Auto-select the new patient
                const newPatient = response.data;
                handlePatientSelect(newPatient);

                // Close modal and reset form
                setShowPatientForm(false);
                resetPatientForm();
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Error al registrar paciente');
        } finally {
            setLoading(false);
        }
    };

    const resetPatientForm = () => {
        setPatientFormData({
            firstName: '',
            secondName: '',
            firstLastName: '',
            secondLastName: '',
            sex: 'masculino',
            birthDate: '',
            documentType: 'cedula',
            documentPrefix: 'V',
            documentNumber: '',
            nationality: 'Venezolana',
            state: 'Distrito Capital',
            municipality: '',
            parish: '',
            city: 'Caracas',
            street: '',
            houseNumber: '',
            address: '',
            phone: '',
            email: '',
            paymentMode: 'particular'
        });
        setAvailableMunicipalities([]);
        setAvailableParishes([]);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNestedChange = (section, field, value, subfield = null) => {
        setFormData(prev => ({
            ...prev,
            [section]: subfield
                ? {
                    ...prev[section],
                    [field]: {
                        ...prev[section][field],
                        [subfield]: value
                    }
                }
                : {
                    ...prev[section],
                    [field]: value
                }
        }));
    };

    const handleRefractionChange = (section, eye, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [eye]: {
                    ...prev[section][eye],
                    [field]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');

            console.log('=== ENVIANDO DATOS AL SERVIDOR ===');
            console.log('FormData completo:', JSON.stringify(formData, null, 2));
            console.log('Campos importantes:', {
                diagnosis: formData.diagnosis,
                medications: formData.medications,
                indicationsRecommendations: formData.indicationsRecommendations,
                lensType: formData.lensType,
                filter: formData.filter
            });

            await medicalHistoryService.create(formData);

            await fetchHistories();
            setShowForm(false);
            resetForm();
            alert('Historia médica creada exitosamente');
        } catch (err) {
            console.error('=== ERROR AL GUARDAR ===');
            console.error('Error completo:', err);
            console.error('Error response:', err.response);
            console.error('Error response data:', err.response?.data);
            console.error('Error status:', err.response?.status);
            console.error('Error message:', err.message);

            let errorMessage = 'Error al crear la historia médica';

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = `Error: ${err.message}`;
            }

            // Add more details if validation error
            if (err.response?.data?.errors) {
                errorMessage += '\n\nDetalles: ' + JSON.stringify(err.response.data.errors);
            }

            setError(errorMessage);

            // Scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // If it's a duplicate history error, show a more prominent alert
            if (err.response?.status === 400 && errorMessage.includes('Ya existe una historia médica')) {
                alert(`⚠️ ERROR: HISTORIA DUPLICADA\n\n${errorMessage}\n\nPor favor, busque la historia existente en el listado para actualizarla.`);
            }

            // Show alert with error for immediate visibility
            alert(`❌ ERROR AL GUARDAR\n\n${errorMessage}\n\nPor favor revisa la consola del navegador (F12) para más detalles.`);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        const initialData = {
            patient: '',
            doctor: '',
            specialty: '',
            consultDate: new Date().toISOString().split('T')[0],
            consultReason: '',
            patientOccupation: '',
            referredBy: '',
            ophthalmologicHistory: {
                previousSurgeries: '',
                ocularTrauma: '',
                previousEyeDiseases: '',
                ophthalmologicMedications: '',
                contactLensHistory: '',
                lastEyeExam: ''
            },
            medicalHistory: {
                systemicDiseases: '',
                allergies: '',
                previousSurgeriesHospitalizations: '',
                currentMedications: ''
            },
            familyHistory: '',
            visualAcuity: { rightEye: '', leftEye: '' },
            refractionPreCycle: {
                rightEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' },
                leftEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' }
            },
            cycloplegia: {
                rightEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' },
                leftEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' }
            },
            finalCorrection: {
                rightEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' },
                leftEye: { sphere: '', cylinder: '', axis: '', visualAcuity: '' }
            },
            addition: { rightEye: '', leftEye: '' },
            lensType: 'Vision sencilla',
            height: '',
            dip: '',
            filter: 'Anti-reflejo',
            biomicroscopy: { rightEyeImage: '', leftEyeImage: '', notes: '' },
            tearBreakupTime: { rightEye: '', leftEye: '' },
            intraocularPressure: { rightEye: '', leftEye: '' },
            fundoscopy: { rightEye: '', leftEye: '' },
            diagnosis: '',
            medications: '',
            indicationsRecommendations: '',
            nextControlDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
        };

        // If user is a doctor, pre-fill doctor and specialty
        if (currentDoctor) {
            initialData.doctor = currentDoctor._id;
            initialData.specialty = currentDoctor.specialty[0] || '';
        }

        setFormData(initialData);
        setSelectedPatient(null);
        setPatientSearch('');
        setFilteredPatients([]);
        setShowPatientDropdown(false);
    };

    const renderRefractionTable = (section, title) => {
        return (
            <div className="refraction-section">
                <h4>{title}</h4>
                <table className="refraction-table">
                    <thead>
                        <tr>
                            <th>Ojo</th>
                            <th>Esfera</th>
                            <th>Cilindro</th>
                            <th>Eje</th>
                            <th>AV</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="eye-label">OD</td>
                            <td>
                                <input
                                    type="number"
                                    step="0.25"
                                    value={formData[section].rightEye.sphere}
                                    onChange={(e) => handleRefractionChange(section, 'rightEye', 'sphere', e.target.value)}
                                    placeholder="0.00"
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    step="0.25"
                                    value={formData[section].rightEye.cylinder}
                                    onChange={(e) => handleRefractionChange(section, 'rightEye', 'cylinder', e.target.value)}
                                    placeholder="0.00"
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    min="0"
                                    max="180"
                                    value={formData[section].rightEye.axis}
                                    onChange={(e) => handleRefractionChange(section, 'rightEye', 'axis', e.target.value)}
                                    placeholder="0"
                                />
                            </td>
                            <td>
                                <div className="av-input-wrapper">
                                    <span className="av-prefix">20/</span>
                                    <input
                                        type="text"
                                        className="av-input"
                                        value={formData[section].rightEye.visualAcuity}
                                        onChange={(e) => handleRefractionChange(section, 'rightEye', 'visualAcuity', e.target.value)}
                                        placeholder=""
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td className="eye-label">OI</td>
                            <td>
                                <input
                                    type="number"
                                    step="0.25"
                                    value={formData[section].leftEye.sphere}
                                    onChange={(e) => handleRefractionChange(section, 'leftEye', 'sphere', e.target.value)}
                                    placeholder="0.00"
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    step="0.25"
                                    value={formData[section].leftEye.cylinder}
                                    onChange={(e) => handleRefractionChange(section, 'leftEye', 'cylinder', e.target.value)}
                                    placeholder="0.00"
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    min="0"
                                    max="180"
                                    value={formData[section].leftEye.axis}
                                    onChange={(e) => handleRefractionChange(section, 'leftEye', 'axis', e.target.value)}
                                    placeholder="0"
                                />
                            </td>
                            <td>
                                <div className="av-input-wrapper">
                                    <span className="av-prefix">20/</span>
                                    <input
                                        type="text"
                                        className="av-input"
                                        value={formData[section].leftEye.visualAcuity}
                                        onChange={(e) => handleRefractionChange(section, 'leftEye', 'visualAcuity', e.target.value)}
                                        placeholder=""
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const handleViewDetails = async (historyId) => {
        try {
            setLoading(true);
            const response = await medicalHistoryService.getById(historyId);
            setSelectedHistory(response.data);
            setShowDetailsModal(true);
        } catch (err) {
            console.error('Error loading history details:', err);
            alert('Error al cargar los detalles de la historia médica');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedHistory(null);
    };

    const handleDownloadRecipe = async (historyId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://col2c.com/api'}/medical-history/${historyId}/recipe`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al descargar recipe');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `receta-medica-${historyId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar la receta médica');
        }
    };

    const handleDownloadReport = async (historyId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://col2c.com/api'}/medical-history/${historyId}/report`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al descargar informe');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `informe-medico-${historyId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar el informe médico');
        }
    };

    const handleDownloadGlasses = async (historyId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://col2c.com/api'}/medical-history/${historyId}/glasses`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al descargar fórmula');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `formula-lentes-${historyId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar la fórmula de lentes');
        }
    };

    const handleDownloadMedicalHistory = async (historyId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://col2c.com/api'}/medical-history/${historyId}/full`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al descargar historia médica');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `historia-medica-completa-${historyId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar la historia médica completa');
        }
    };

    const handleDownloadFullHistory = async (historyId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://col2c.com/api'}/medical-history/${historyId}/full`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Error al descargar historia completa');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `historia-medica-completa-${historyId}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar la historia médica completa');
        }
    };

    const handlePrint = () => {
        // Verificar que el usuario sea administrador o médico
        if (!isAdmin && !isDoctor) {
            alert('No tienes permisos para imprimir historias médicas');
            return;
        }

        // Descargar la historia médica completa en Word
        if (selectedHistory && selectedHistory._id) {
            handleDownloadFullHistory(selectedHistory._id);
        }
    };

    const handleDelete = async (historyId) => {
        // Solo administradores pueden eliminar
        if (!isAdmin) {
            alert('Solo los administradores pueden eliminar historias médicas');
            return;
        }

        // Confirmar eliminación
        const confirmDelete = window.confirm(
            '¿Está seguro que desea eliminar esta historia médica?\n\n' +
            'Esta acción no se puede deshacer y eliminará permanentemente todos los datos de la historia médica.'
        );

        if (!confirmDelete) {
            return;
        }

        try {
            setLoading(true);
            await medicalHistoryService.delete(historyId);

            // Actualizar la lista de historias
            setHistories(histories.filter(h => h._id !== historyId));

            // Cerrar modal si está abierto
            if (showDetailsModal && selectedHistory?._id === historyId) {
                setShowDetailsModal(false);
                setSelectedHistory(null);
            }

            alert('Historia médica eliminada exitosamente');
        } catch (err) {
            console.error('Error deleting medical history:', err);
            setError(err.response?.data?.message || 'Error al eliminar la historia médica');
        } finally {
            setLoading(false);
        }
    };

    const filteredHistories = histories.filter(history => {
        if (!searchTerm) return true;
        const patient = history.patient;
        const searchLower = searchTerm.toLowerCase();

        return (
            patient?.firstName?.toLowerCase().includes(searchLower) ||
            patient?.firstLastName?.toLowerCase().includes(searchLower) ||
            patient?.documentNumber?.includes(searchTerm) ||
            history.primaryDiagnosis?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="medical-history-container">
            <div className="page-header">
                <h1>Historias Médicas</h1>
                <div className="page-header-actions">
                    <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
                        ← Volver al Dashboard
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            if (showForm) {
                                resetForm();
                            }
                            setShowForm(!showForm);
                        }}
                    >
                        {showForm ? 'Cancelar' : '+ Nueva Historia'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error" style={{
                    backgroundColor: '#fee',
                    border: '2px solid #c00',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    fontSize: '1rem',
                    fontWeight: '500'
                }}>
                    <strong>❌ Error:</strong> {error}
                    <br />
                    <small style={{ marginTop: '0.5rem', display: 'block', color: '#666' }}>
                        Por favor revisa la consola del navegador (F12) para más detalles
                    </small>
                </div>
            )}

            {showForm ? (
                <div className="form-container">
                    <form onSubmit={handleSubmit} className="medical-history-form">
                        {/* SECCIÓN 1: Datos del Paciente y Consulta */}
                        <section className="form-section">
                            <h3>1. Información de la Consulta</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Paciente *</label>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <div className="search-container" style={{ position: 'relative', flex: 1 }}>
                                            <input
                                                type="text"
                                                value={patientSearch}
                                                onChange={(e) => {
                                                    setPatientSearch(e.target.value);
                                                    setShowPatientDropdown(true);
                                                    if (e.target.value.trim() === '') {
                                                        setFormData(prev => ({ ...prev, patient: '' }));
                                                        setSelectedPatient(null);
                                                    }
                                                }}
                                                onFocus={() => {
                                                    if (patientSearch && filteredPatients.length > 0) {
                                                        setShowPatientDropdown(true);
                                                    }
                                                }}
                                                placeholder="Buscar por nombre o cédula..."
                                                required
                                            />
                                            {showPatientDropdown && filteredPatients.length > 0 && (
                                                <div className="search-dropdown" style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    zIndex: 1000,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }}>
                                                    {filteredPatients.map(patient => (
                                                        <div
                                                            key={patient._id}
                                                            className="search-item"
                                                            onClick={() => handlePatientSelect(patient)}
                                                            style={{
                                                                padding: '0.75rem',
                                                                cursor: 'pointer',
                                                                borderBottom: '1px solid #eee'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                                                        >
                                                            <strong>{patient.firstName} {patient.secondName || ''} {patient.firstLastName} {patient.secondLastName || ''}</strong>
                                                            <br />
                                                            <small style={{ color: '#666' }}>Cédula: {patient.documentNumber}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showPatientDropdown && patientSearch && filteredPatients.length === 0 && (
                                                <div className="search-dropdown" style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    zIndex: 1000,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                    padding: '0.75rem',
                                                    textAlign: 'center',
                                                    color: '#666'
                                                }}>
                                                    No se encontraron pacientes
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowPatientForm(true)}
                                            className="btn btn-secondary"
                                            style={{
                                                padding: '0.75rem 1rem',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            + Registrar Paciente
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Médico *</label>
                                    <select
                                        name="doctor"
                                        value={formData.doctor}
                                        onChange={handleInputChange}
                                        required
                                        disabled={user?.role === 'medico'}
                                        style={user?.role === 'medico' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    >
                                        <option value="">Seleccione un médico</option>
                                        {doctors.map(doctor => (
                                            <option key={doctor._id} value={doctor._id}>
                                                {getDoctorPrefix(doctor)} {doctor.firstName} {doctor.firstLastName} - {doctor.specialty}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Especialidad *</label>
                                    <input
                                        type="text"
                                        name="specialty"
                                        value={formData.specialty}
                                        onChange={handleInputChange}
                                        required
                                        disabled={user?.role === 'medico'}
                                        style={user?.role === 'medico' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Consulta *</label>
                                    <input
                                        type="date"
                                        name="consultDate"
                                        value={formData.consultDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Ocupación del Paciente</label>
                                    <input
                                        type="text"
                                        name="patientOccupation"
                                        value={formData.patientOccupation}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Referido por</label>
                                    <input
                                        type="text"
                                        name="referredBy"
                                        value={formData.referredBy}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Motivo de Consulta *</label>
                                    <textarea
                                        name="consultReason"
                                        value={formData.consultReason}
                                        onChange={handleInputChange}
                                        rows="3"
                                        required
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 2: Antecedentes Oftalmológicos */}
                        <section className="form-section">
                            <h3>2. Antecedentes Oftalmológicos</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Cirugías Previas</label>
                                    <textarea
                                        value={formData.ophthalmologicHistory.previousSurgeries}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'previousSurgeries', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Traumatismos Oculares</label>
                                    <textarea
                                        value={formData.ophthalmologicHistory.ocularTrauma}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'ocularTrauma', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Enfermedades Oculares Previas</label>
                                    <textarea
                                        value={formData.ophthalmologicHistory.previousEyeDiseases}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'previousEyeDiseases', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Medicamentos Oftalmológicos</label>
                                    <textarea
                                        value={formData.ophthalmologicHistory.ophthalmologicMedications}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'ophthalmologicMedications', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Uso de Lentes de Contacto</label>
                                    <input
                                        type="text"
                                        value={formData.ophthalmologicHistory.contactLensHistory}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'contactLensHistory', e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Último Examen Oftalmológico</label>
                                    <input
                                        type="date"
                                        value={formData.ophthalmologicHistory.lastEyeExam}
                                        onChange={(e) => handleNestedChange('ophthalmologicHistory', 'lastEyeExam', e.target.value)}
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 3: Antecedentes Médicos Generales */}
                        <section className="form-section">
                            <h3>3. Antecedentes Médicos Generales</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Enfermedades Sistémicas</label>
                                    <textarea
                                        value={formData.medicalHistory.systemicDiseases}
                                        onChange={(e) => handleNestedChange('medicalHistory', 'systemicDiseases', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Alergias</label>
                                    <textarea
                                        value={formData.medicalHistory.allergies}
                                        onChange={(e) => handleNestedChange('medicalHistory', 'allergies', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Cirugías y Hospitalizaciones Previas</label>
                                    <textarea
                                        value={formData.medicalHistory.previousSurgeriesHospitalizations}
                                        onChange={(e) => handleNestedChange('medicalHistory', 'previousSurgeriesHospitalizations', e.target.value)}
                                        rows="2"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Medicamentos Actuales</label>
                                    <textarea
                                        value={formData.medicalHistory.currentMedications}
                                        onChange={(e) => handleNestedChange('medicalHistory', 'currentMedications', e.target.value)}
                                        rows="2"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 4: Antecedentes Familiares */}
                        <section className="form-section">
                            <h3>4. Antecedentes Familiares</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Antecedentes Familiares</label>
                                    <textarea
                                        name="familyHistory"
                                        value={formData.familyHistory}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Describa enfermedades oftalmológicas en la familia"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 5: Agudeza Visual */}
                        <section className="form-section">
                            <h3>5. Agudeza Visual</h3>
                            <div className="form-grid two-columns">
                                <div className="form-group">
                                    <label>Ojo Derecho (OD)</label>
                                    <input
                                        type="text"
                                        value={formData.visualAcuity.rightEye}
                                        onChange={(e) => handleNestedChange('visualAcuity', 'rightEye', e.target.value)}
                                        placeholder="Ej: 20/20"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Ojo Izquierdo (OI)</label>
                                    <input
                                        type="text"
                                        value={formData.visualAcuity.leftEye}
                                        onChange={(e) => handleNestedChange('visualAcuity', 'leftEye', e.target.value)}
                                        placeholder="Ej: 20/40"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 6: Refracción Pre-Ciclo */}
                        <section className="form-section">
                            <h3>6. Refracción Pre-Ciclo</h3>
                            {renderRefractionTable('refractionPreCycle', '')}
                        </section>

                        {/* SECCIÓN 7: Cicloplejía */}
                        <section className="form-section">
                            <h3>7. Cicloplejía</h3>
                            {renderRefractionTable('cycloplegia', '')}
                        </section>

                        {/* SECCIÓN 8: FC (Final con Corrección) */}
                        <section className="form-section">
                            <h3>8. FC (Final con Corrección)</h3>
                            {renderRefractionTable('finalCorrection', '')}
                        </section>

                        {/* SECCIÓN 9: Adición */}
                        <section className="form-section">
                            <h3>9. Adición (Add)</h3>
                            <div className="refraction-section">
                                <table className="refraction-table">
                                    <thead>
                                        <tr>
                                            <th>Ojo</th>
                                            <th>Esfera</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="eye-label">OD</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    step="0.25"
                                                    value={formData.addition.rightEye}
                                                    onChange={(e) => handleNestedChange('addition', 'rightEye', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="eye-label">OI</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    step="0.25"
                                                    value={formData.addition.leftEye}
                                                    onChange={(e) => handleNestedChange('addition', 'leftEye', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* SECCIÓN 9.5: Información de Lentes */}
                        <section className="form-section">
                            <h3>9.5. Información de Lentes</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Tipo de Lente</label>
                                    <input
                                        type="text"
                                        list="lensTypeList"
                                        value={formData.lensType}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            lensType: e.target.value
                                        }))}
                                        placeholder="Seleccionar o escribir"
                                    />
                                    <datalist id="lensTypeList">
                                        <option value="Vision sencilla" />
                                        <option value="Bifocal" />
                                        <option value="Progresivo" />
                                        <option value="Multifocal" />
                                        <option value="Ocupacional" />
                                    </datalist>
                                </div>

                                <div className="form-group">
                                    <label>Altura</label>
                                    <input
                                        type="text"
                                        value={formData.height}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            height: e.target.value
                                        }))}
                                        placeholder="Ejemplo: 18mm"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>DIP (Distancia Inter-Pupilar)</label>
                                    <input
                                        type="text"
                                        value={formData.dip}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            dip: e.target.value
                                        }))}
                                        placeholder="Ejemplo: 62mm"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Filtro</label>
                                    <input
                                        type="text"
                                        list="filterList"
                                        value={formData.filter}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            filter: e.target.value
                                        }))}
                                        placeholder="Seleccionar o escribir"
                                    />
                                    <datalist id="filterList">
                                        <option value="Sin filtro" />
                                        <option value="Anti-reflejo" />
                                        <option value="Fotocromático" />
                                        <option value="Polarizado" />
                                        <option value="UV" />
                                        <option value="Blue Light" />
                                        <option value="Transitions" />
                                    </datalist>
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 10: Biomicroscopía */}
                        <section className="form-section">
                            <h3>10. Biomicroscopía</h3>

                            <div className="eye-canvas-grid">
                                <EyeCanvas
                                    eye="right"
                                    value={formData.biomicroscopy.rightEyeImage}
                                    onChange={(imageData) => handleNestedChange('biomicroscopy', 'rightEyeImage', imageData)}
                                />

                                <EyeCanvas
                                    eye="left"
                                    value={formData.biomicroscopy.leftEyeImage}
                                    onChange={(imageData) => handleNestedChange('biomicroscopy', 'leftEyeImage', imageData)}
                                />
                            </div>

                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Notas</label>
                                    <textarea
                                        value={formData.biomicroscopy.notes || ''}
                                        onChange={(e) => handleNestedChange('biomicroscopy', 'notes', e.target.value)}
                                        rows="3"
                                        placeholder="Describa hallazgos y observaciones de la biomicroscopía"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 11: BUT (Tiempo de Ruptura Lagrimal) */}
                        <section className="form-section">
                            <h3>11. BUT (Tiempo de Ruptura Lagrimal)</h3>
                            <div className="form-grid two-columns">
                                <div className="form-group">
                                    <label>OD (segundos)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.tearBreakupTime.rightEye}
                                        onChange={(e) => handleNestedChange('tearBreakupTime', 'rightEye', e.target.value)}
                                        placeholder="0.0"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>OI (segundos)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.tearBreakupTime.leftEye}
                                        onChange={(e) => handleNestedChange('tearBreakupTime', 'leftEye', e.target.value)}
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 12: Presión Intraocular */}
                        <section className="form-section">
                            <h3>12. Presión Intraocular (PIO)</h3>
                            <div className="form-grid two-columns">
                                <div className="form-group">
                                    <label>OD (mmHg)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.intraocularPressure.rightEye}
                                        onChange={(e) => handleNestedChange('intraocularPressure', 'rightEye', e.target.value)}
                                        placeholder="0.0"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>OI (mmHg)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={formData.intraocularPressure.leftEye}
                                        onChange={(e) => handleNestedChange('intraocularPressure', 'leftEye', e.target.value)}
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 13: Fondo de Ojo */}
                        <section className="form-section">
                            <h3>13. Fondo de Ojo</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Ojo Derecho (OD)</label>
                                    <textarea
                                        value={formData.fundoscopy.rightEye}
                                        onChange={(e) => handleNestedChange('fundoscopy', 'rightEye', e.target.value)}
                                        rows="3"
                                        placeholder="Describa hallazgos"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Ojo Izquierdo (OI)</label>
                                    <textarea
                                        value={formData.fundoscopy.leftEye}
                                        onChange={(e) => handleNestedChange('fundoscopy', 'leftEye', e.target.value)}
                                        rows="3"
                                        placeholder="Describa hallazgos"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 14: Diagnóstico */}
                        <section className="form-section">
                            <h3>14. Diagnóstico</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Diagnóstico *</label>
                                    <textarea
                                        name="diagnosis"
                                        value={formData.diagnosis}
                                        onChange={handleInputChange}
                                        rows="3"
                                        required
                                        placeholder="Describa el diagnóstico del paciente"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 15: Medicamentos Recetados */}
                        <section className="form-section">
                            <h3>15. Medicamentos Recetados</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Medicamentos</label>
                                    <textarea
                                        name="medications"
                                        value={formData.medications}
                                        onChange={handleInputChange}
                                        rows="5"
                                        placeholder="Liste los medicamentos recetados con su dosificación&#10;Ejemplo:&#10;- Latanoprost 0.005% - 1 gota cada noche en ambos ojos&#10;- Timolol 0.5% - 1 gota cada 12 horas en ojo derecho"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 16: Indicaciones y Recomendaciones */}
                        <section className="form-section">
                            <h3>16. Indicaciones y Recomendaciones</h3>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Indicaciones y Recomendaciones</label>
                                    <textarea
                                        name="indicationsRecommendations"
                                        value={formData.indicationsRecommendations}
                                        onChange={handleInputChange}
                                        rows="4"
                                        placeholder="Instrucciones de uso de medicamentos, recomendaciones generales, cuidados especiales, etc."
                                    />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 17: Próximo Control */}
                        <section className="form-section">
                            <h3>17. Próximo Control</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Fecha del Próximo Control *</label>
                                    <input
                                        type="date"
                                        name="nextControlDate"
                                        value={formData.nextControlDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                                        Por defecto: 1 año después de la consulta actual
                                    </small>
                                </div>
                            </div>
                        </section>

                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => {
                                setShowForm(false);
                                resetForm();
                            }}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? 'Guardando...' : 'Guardar Historia Médica'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="histories-list">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Buscar por paciente, cédula o diagnóstico..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {!searchTerm.trim() ? (
                        <div className="no-data" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <p>🔍 Por favor, ingrese un término de búsqueda para proteger la privacidad de los pacientes.</p>
                            <p style={{ fontSize: '14px', marginTop: '10px' }}>
                                Puede buscar por nombre del paciente, número de cédula o diagnóstico.
                            </p>
                        </div>
                    ) : loading ? (
                        <div className="loading">Cargando historias médicas...</div>
                    ) : filteredHistories.length === 0 ? (
                        <div className="no-data">
                            <p>No se encontraron historias médicas que coincidan con la búsqueda</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Paciente</th>
                                        <th>Médico</th>
                                        <th>Especialidad</th>
                                        <th>Diagnóstico</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistories.map(history => (
                                        <tr key={history._id}>
                                            <td>{new Date(history.consultDate).toLocaleDateString()}</td>
                                            <td>
                                                {history.patient?.firstName} {history.patient?.firstLastName}
                                                <br />
                                                <small>{history.patient?.documentNumber}</small>
                                            </td>
                                            <td>{getDoctorPrefix(history.doctor)} {history.doctor?.firstName} {history.doctor?.firstLastName}</td>
                                            <td>{history.specialty}</td>
                                            <td>{history.primaryDiagnosis}</td>
                                            <td>
                                                <span className={`status-badge status-${history.status}`}>
                                                    {history.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => handleViewDetails(history._id)}
                                                        title="Ver detalles"
                                                    >
                                                        👁️
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(history._id)}
                                                            title="Eliminar historia médica"
                                                            style={{ marginLeft: '5px' }}
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedHistory && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    overflowY: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '10px',
                        maxWidth: '900px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2>Detalles de Historia Médica</h2>
                            <button onClick={handleCloseDetailsModal} className="btn btn-secondary">✕</button>
                        </div>

                        <div style={{ fontSize: '14px' }}>
                            {/* Información del Paciente */}
                            <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                <h3>Información del Paciente</h3>
                                <p><strong>Nombre:</strong> {selectedHistory.patient?.firstName} {selectedHistory.patient?.secondName || ''} {selectedHistory.patient?.firstLastName} {selectedHistory.patient?.secondLastName || ''}</p>
                                {selectedHistory.patient?.documentNumber && <p><strong>Documento:</strong> {selectedHistory.patient?.documentPrefix || ''}-{selectedHistory.patient.documentNumber}</p>}
                                {selectedHistory.patient?.sex && <p><strong>Sexo:</strong> {selectedHistory.patient.sex}</p>}
                                {selectedHistory.patient?.birthDate && <p><strong>Fecha de Nacimiento:</strong> {new Date(selectedHistory.patient.birthDate).toLocaleDateString()}</p>}
                                {selectedHistory.patient?.phone && <p><strong>Teléfono:</strong> {selectedHistory.patient.phone}</p>}
                                {selectedHistory.patient?.email && <p><strong>Email:</strong> {selectedHistory.patient.email}</p>}
                                {/* Dirección del Paciente */}
                                {(selectedHistory.patient?.state || selectedHistory.patient?.city || selectedHistory.patient?.address ||
                                  selectedHistory.patient?.street || selectedHistory.patient?.municipality || selectedHistory.patient?.parish) && (
                                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                                        <p><strong>Dirección:</strong></p>
                                        {selectedHistory.patient?.street && <p style={{ marginLeft: '20px' }}>Calle: {selectedHistory.patient.street}</p>}
                                        {selectedHistory.patient?.houseNumber && <p style={{ marginLeft: '20px' }}>Casa/Edificio: {selectedHistory.patient.houseNumber}</p>}
                                        {selectedHistory.patient?.address && <p style={{ marginLeft: '20px' }}>Referencia: {selectedHistory.patient.address}</p>}
                                        {selectedHistory.patient?.parish && <p style={{ marginLeft: '20px' }}>Parroquia: {selectedHistory.patient.parish}</p>}
                                        {selectedHistory.patient?.municipality && <p style={{ marginLeft: '20px' }}>Municipio: {selectedHistory.patient.municipality}</p>}
                                        {selectedHistory.patient?.state && <p style={{ marginLeft: '20px' }}>Estado: {selectedHistory.patient.state}</p>}
                                        {selectedHistory.patient?.city && <p style={{ marginLeft: '20px' }}>Ciudad: {selectedHistory.patient.city}</p>}
                                    </div>
                                )}
                            </section>

                            {/* Información de la Consulta */}
                            <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                <h3>Información de la Consulta</h3>
                                <p><strong>Médico:</strong> {getDoctorPrefix(selectedHistory.doctor)} {selectedHistory.doctor?.firstName} {selectedHistory.doctor?.firstLastName}</p>
                                {selectedHistory.specialty && <p><strong>Especialidad:</strong> {selectedHistory.specialty}</p>}
                                {selectedHistory.consultDate && <p><strong>Fecha de Consulta:</strong> {new Date(selectedHistory.consultDate).toLocaleDateString()}</p>}
                                {selectedHistory.consultReason && <p><strong>Motivo de Consulta:</strong> {selectedHistory.consultReason}</p>}
                                {selectedHistory.patientOccupation && <p><strong>Ocupación:</strong> {selectedHistory.patientOccupation}</p>}
                                {selectedHistory.referredBy && <p><strong>Referido por:</strong> {selectedHistory.referredBy}</p>}
                            </section>

                            {/* Antecedentes Oftalmológicos - Solo mostrar si hay datos */}
                            {(selectedHistory.ophthalmologicHistory?.previousSurgeries ||
                              selectedHistory.ophthalmologicHistory?.ocularTrauma ||
                              selectedHistory.ophthalmologicHistory?.previousEyeDiseases ||
                              selectedHistory.ophthalmologicHistory?.ophthalmologicMedications ||
                              selectedHistory.ophthalmologicHistory?.contactLensHistory ||
                              selectedHistory.ophthalmologicHistory?.lastEyeExam) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Antecedentes Oftalmológicos</h3>
                                    {selectedHistory.ophthalmologicHistory?.previousSurgeries && <p><strong>Cirugías Previas:</strong> {selectedHistory.ophthalmologicHistory.previousSurgeries}</p>}
                                    {selectedHistory.ophthalmologicHistory?.ocularTrauma && <p><strong>Trauma Ocular:</strong> {selectedHistory.ophthalmologicHistory.ocularTrauma}</p>}
                                    {selectedHistory.ophthalmologicHistory?.previousEyeDiseases && <p><strong>Enfermedades Oculares Previas:</strong> {selectedHistory.ophthalmologicHistory.previousEyeDiseases}</p>}
                                    {selectedHistory.ophthalmologicHistory?.ophthalmologicMedications && <p><strong>Medicamentos Oftalmológicos:</strong> {selectedHistory.ophthalmologicHistory.ophthalmologicMedications}</p>}
                                    {selectedHistory.ophthalmologicHistory?.contactLensHistory && <p><strong>Uso de Lentes de Contacto:</strong> {selectedHistory.ophthalmologicHistory.contactLensHistory}</p>}
                                    {selectedHistory.ophthalmologicHistory?.lastEyeExam && <p><strong>Último Examen Oftalmológico:</strong> {new Date(selectedHistory.ophthalmologicHistory.lastEyeExam).toLocaleDateString()}</p>}
                                </section>
                            )}

                            {/* Antecedentes Médicos - Solo mostrar si hay datos */}
                            {(selectedHistory.medicalHistory?.systemicDiseases ||
                              selectedHistory.medicalHistory?.allergies ||
                              selectedHistory.medicalHistory?.previousSurgeriesHospitalizations ||
                              selectedHistory.medicalHistory?.currentMedications) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Antecedentes Médicos Generales</h3>
                                    {selectedHistory.medicalHistory?.systemicDiseases && <p><strong>Enfermedades Sistémicas:</strong> {selectedHistory.medicalHistory.systemicDiseases}</p>}
                                    {selectedHistory.medicalHistory?.allergies && <p><strong>Alergias:</strong> {selectedHistory.medicalHistory.allergies}</p>}
                                    {selectedHistory.medicalHistory?.previousSurgeriesHospitalizations && <p><strong>Cirugías y Hospitalizaciones Previas:</strong> {selectedHistory.medicalHistory.previousSurgeriesHospitalizations}</p>}
                                    {selectedHistory.medicalHistory?.currentMedications && <p><strong>Medicamentos Actuales:</strong> {selectedHistory.medicalHistory.currentMedications}</p>}
                                </section>
                            )}

                            {/* Antecedentes Familiares */}
                            {selectedHistory.familyHistory && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Antecedentes Familiares</h3>
                                    <p>{selectedHistory.familyHistory}</p>
                                </section>
                            )}

                            {/* Agudeza Visual */}
                            {(selectedHistory.visualAcuity?.rightEye || selectedHistory.visualAcuity?.leftEye) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Agudeza Visual</h3>
                                    {selectedHistory.visualAcuity?.rightEye && <p><strong>Ojo Derecho (OD):</strong> {selectedHistory.visualAcuity.rightEye}</p>}
                                    {selectedHistory.visualAcuity?.leftEye && <p><strong>Ojo Izquierdo (OI):</strong> {selectedHistory.visualAcuity.leftEye}</p>}
                                </section>
                            )}

                            {/* Refracción Pre-Ciclo */}
                            {(selectedHistory.refractionPreCycle?.rightEye?.sphere ||
                              selectedHistory.refractionPreCycle?.rightEye?.cylinder ||
                              selectedHistory.refractionPreCycle?.leftEye?.sphere ||
                              selectedHistory.refractionPreCycle?.leftEye?.cylinder) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Refracción Pre-Ciclo</h3>
                                    {(selectedHistory.refractionPreCycle?.rightEye?.sphere || selectedHistory.refractionPreCycle?.rightEye?.cylinder) && (
                                        <p><strong>OD:</strong> Esf: {selectedHistory.refractionPreCycle.rightEye.sphere || 'N/A'} / Cil: {selectedHistory.refractionPreCycle.rightEye.cylinder || 'N/A'} / Eje: {selectedHistory.refractionPreCycle.rightEye.axis || 'N/A'} / AV: {selectedHistory.refractionPreCycle.rightEye.visualAcuity || 'N/A'}</p>
                                    )}
                                    {(selectedHistory.refractionPreCycle?.leftEye?.sphere || selectedHistory.refractionPreCycle?.leftEye?.cylinder) && (
                                        <p><strong>OI:</strong> Esf: {selectedHistory.refractionPreCycle.leftEye.sphere || 'N/A'} / Cil: {selectedHistory.refractionPreCycle.leftEye.cylinder || 'N/A'} / Eje: {selectedHistory.refractionPreCycle.leftEye.axis || 'N/A'} / AV: {selectedHistory.refractionPreCycle.leftEye.visualAcuity || 'N/A'}</p>
                                    )}
                                </section>
                            )}

                            {/* Cicloplejía */}
                            {(selectedHistory.cycloplegia?.rightEye?.sphere ||
                              selectedHistory.cycloplegia?.rightEye?.cylinder ||
                              selectedHistory.cycloplegia?.leftEye?.sphere ||
                              selectedHistory.cycloplegia?.leftEye?.cylinder) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Cicloplejía</h3>
                                    {(selectedHistory.cycloplegia?.rightEye?.sphere || selectedHistory.cycloplegia?.rightEye?.cylinder) && (
                                        <p><strong>OD:</strong> Esf: {selectedHistory.cycloplegia.rightEye.sphere || 'N/A'} / Cil: {selectedHistory.cycloplegia.rightEye.cylinder || 'N/A'} / Eje: {selectedHistory.cycloplegia.rightEye.axis || 'N/A'} / AV: {selectedHistory.cycloplegia.rightEye.visualAcuity || 'N/A'}</p>
                                    )}
                                    {(selectedHistory.cycloplegia?.leftEye?.sphere || selectedHistory.cycloplegia?.leftEye?.cylinder) && (
                                        <p><strong>OI:</strong> Esf: {selectedHistory.cycloplegia.leftEye.sphere || 'N/A'} / Cil: {selectedHistory.cycloplegia.leftEye.cylinder || 'N/A'} / Eje: {selectedHistory.cycloplegia.leftEye.axis || 'N/A'} / AV: {selectedHistory.cycloplegia.leftEye.visualAcuity || 'N/A'}</p>
                                    )}
                                </section>
                            )}

                            {/* Final con Corrección */}
                            {(selectedHistory.finalCorrection?.rightEye?.sphere ||
                              selectedHistory.finalCorrection?.rightEye?.cylinder ||
                              selectedHistory.finalCorrection?.leftEye?.sphere ||
                              selectedHistory.finalCorrection?.leftEye?.cylinder) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Final con Corrección (FC)</h3>
                                    {(selectedHistory.finalCorrection?.rightEye?.sphere || selectedHistory.finalCorrection?.rightEye?.cylinder) && (
                                        <p><strong>OD:</strong> Esf: {selectedHistory.finalCorrection.rightEye.sphere || 'N/A'} / Cil: {selectedHistory.finalCorrection.rightEye.cylinder || 'N/A'} / Eje: {selectedHistory.finalCorrection.rightEye.axis || 'N/A'} / AV: {selectedHistory.finalCorrection.rightEye.visualAcuity || 'N/A'}</p>
                                    )}
                                    {(selectedHistory.finalCorrection?.leftEye?.sphere || selectedHistory.finalCorrection?.leftEye?.cylinder) && (
                                        <p><strong>OI:</strong> Esf: {selectedHistory.finalCorrection.leftEye.sphere || 'N/A'} / Cil: {selectedHistory.finalCorrection.leftEye.cylinder || 'N/A'} / Eje: {selectedHistory.finalCorrection.leftEye.axis || 'N/A'} / AV: {selectedHistory.finalCorrection.leftEye.visualAcuity || 'N/A'}</p>
                                    )}
                                </section>
                            )}

                            {/* Adición */}
                            {(selectedHistory.addition?.rightEye || selectedHistory.addition?.leftEye) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Adición (Add)</h3>
                                    {selectedHistory.addition?.rightEye && <p><strong>OD:</strong> {selectedHistory.addition.rightEye}</p>}
                                    {selectedHistory.addition?.leftEye && <p><strong>OI:</strong> {selectedHistory.addition.leftEye}</p>}
                                </section>
                            )}

                            {/* Información de Lentes */}
                            {(selectedHistory.lensType || selectedHistory.height || selectedHistory.dip || selectedHistory.filter) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Información de Lentes</h3>
                                    {selectedHistory.lensType && <p><strong>Tipo de Lente:</strong> {selectedHistory.lensType}</p>}
                                    {selectedHistory.height && <p><strong>Altura:</strong> {selectedHistory.height}</p>}
                                    {selectedHistory.dip && <p><strong>DIP:</strong> {selectedHistory.dip}</p>}
                                    {selectedHistory.filter && <p><strong>Filtro:</strong> {selectedHistory.filter}</p>}
                                </section>
                            )}

                            {/* Biomicroscopía */}
                            {(selectedHistory.biomicroscopy?.rightEyeImage ||
                              selectedHistory.biomicroscopy?.leftEyeImage ||
                              selectedHistory.biomicroscopy?.notes) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Biomicroscopía</h3>
                                    {selectedHistory.biomicroscopy?.rightEyeImage && (
                                        <div style={{ marginBottom: '10px' }}>
                                            <p><strong>Ojo Derecho:</strong></p>
                                            <img src={selectedHistory.biomicroscopy.rightEyeImage} alt="Biomicroscopía OD" style={{ maxWidth: '300px', border: '1px solid #ddd' }} />
                                        </div>
                                    )}
                                    {selectedHistory.biomicroscopy?.leftEyeImage && (
                                        <div style={{ marginBottom: '10px' }}>
                                            <p><strong>Ojo Izquierdo:</strong></p>
                                            <img src={selectedHistory.biomicroscopy.leftEyeImage} alt="Biomicroscopía OI" style={{ maxWidth: '300px', border: '1px solid #ddd' }} />
                                        </div>
                                    )}
                                    {selectedHistory.biomicroscopy?.notes && <p><strong>Notas:</strong> {selectedHistory.biomicroscopy.notes}</p>}
                                </section>
                            )}

                            {/* Tiempo de Ruptura Lagrimal (BUT) */}
                            {(selectedHistory.tearBreakupTime?.rightEye || selectedHistory.tearBreakupTime?.leftEye) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Tiempo de Ruptura Lagrimal (BUT)</h3>
                                    {selectedHistory.tearBreakupTime?.rightEye && <p><strong>OD:</strong> {selectedHistory.tearBreakupTime.rightEye} segundos</p>}
                                    {selectedHistory.tearBreakupTime?.leftEye && <p><strong>OI:</strong> {selectedHistory.tearBreakupTime.leftEye} segundos</p>}
                                </section>
                            )}

                            {/* Presión Intraocular */}
                            {(selectedHistory.intraocularPressure?.rightEye || selectedHistory.intraocularPressure?.leftEye) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Presión Intraocular (PIO)</h3>
                                    {selectedHistory.intraocularPressure?.rightEye && <p><strong>OD:</strong> {selectedHistory.intraocularPressure.rightEye} mmHg</p>}
                                    {selectedHistory.intraocularPressure?.leftEye && <p><strong>OI:</strong> {selectedHistory.intraocularPressure.leftEye} mmHg</p>}
                                </section>
                            )}

                            {/* Fondo de Ojo */}
                            {(selectedHistory.fundoscopy?.rightEye || selectedHistory.fundoscopy?.leftEye) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Fondo de Ojo</h3>
                                    {selectedHistory.fundoscopy?.rightEye && <p><strong>Ojo Derecho (OD):</strong> {selectedHistory.fundoscopy.rightEye}</p>}
                                    {selectedHistory.fundoscopy?.leftEye && <p><strong>Ojo Izquierdo (OI):</strong> {selectedHistory.fundoscopy.leftEye}</p>}
                                </section>
                            )}

                            {/* Diagnóstico y Plan */}
                            {(selectedHistory.diagnosis || selectedHistory.primaryDiagnosis || selectedHistory.secondaryDiagnosis || selectedHistory.medications || selectedHistory.indicationsRecommendations) && (
                                <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                    <h3>Diagnóstico y Plan</h3>
                                    {selectedHistory.diagnosis && <p><strong>Diagnóstico:</strong> {selectedHistory.diagnosis}</p>}
                                    {selectedHistory.primaryDiagnosis && <p><strong>Diagnóstico Principal:</strong> {selectedHistory.primaryDiagnosis}</p>}
                                    {selectedHistory.secondaryDiagnosis && <p><strong>Diagnóstico Secundario:</strong> {selectedHistory.secondaryDiagnosis}</p>}
                                    {selectedHistory.medications && (
                                        <div style={{ marginTop: '10px' }}>
                                            <strong>Medicamentos Recetados:</strong>
                                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', marginTop: '5px', backgroundColor: 'white', padding: '10px', borderRadius: '3px' }}>{selectedHistory.medications}</pre>
                                        </div>
                                    )}
                                    {selectedHistory.indicationsRecommendations && (
                                        <div style={{ marginTop: '10px' }}>
                                            <strong>Indicaciones y Recomendaciones:</strong>
                                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', marginTop: '5px', backgroundColor: 'white', padding: '10px', borderRadius: '3px' }}>{selectedHistory.indicationsRecommendations}</pre>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* Estado y Próximo Control */}
                            <section style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                <h3>Estado de la Historia</h3>
                                <p><strong>Estado:</strong> <span className={`status-badge status-${selectedHistory.status}`}>{selectedHistory.status}</span></p>
                                {selectedHistory.nextControlDate && <p><strong>Próximo Control:</strong> {new Date(selectedHistory.nextControlDate).toLocaleDateString()}</p>}
                            </section>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={() => handleDownloadRecipe(selectedHistory._id)}
                                    className="btn btn-primary"
                                    style={{ fontSize: '14px' }}
                                >
                                    📄 Receta Médica
                                </button>
                                <button
                                    onClick={() => handleDownloadReport(selectedHistory._id)}
                                    className="btn btn-primary"
                                    style={{ fontSize: '14px' }}
                                >
                                    📋 Informe Médico
                                </button>
                                <button
                                    onClick={() => handleDownloadGlasses(selectedHistory._id)}
                                    className="btn btn-primary"
                                    style={{ fontSize: '14px' }}
                                >
                                    👓 Fórmula de Lentes
                                </button>
                                <button
                                    onClick={() => handleDownloadMedicalHistory(selectedHistory._id)}
                                    className="btn btn-primary"
                                    style={{ fontSize: '14px' }}
                                >
                                    📋 Historia Médica
                                </button>
                                {(isAdmin || isDoctor) && (
                                    <button
                                        onClick={handlePrint}
                                        className="btn btn-primary"
                                        style={{ fontSize: '14px' }}
                                    >
                                        🖨️ Imprimir
                                    </button>
                                )}
                            </div>
                            <button onClick={handleCloseDetailsModal} className="btn btn-secondary">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Patient Registration Modal */}
            {showPatientForm && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '2rem',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0, color: '#1d2d48' }}>Registrar Nuevo Paciente</h2>
                            <button
                                type="button"
                                onClick={() => setShowPatientForm(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handlePatientFormSubmit}>
                            {/* Section: Personal Information */}
                            <h3 style={{ color: '#1d2d48', marginTop: '1rem', marginBottom: '1rem' }}>Información Personal</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Primer Nombre *</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={patientFormData.firstName}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Segundo Nombre</label>
                                    <input
                                        type="text"
                                        name="secondName"
                                        value={patientFormData.secondName}
                                        onChange={handlePatientFormInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Primer Apellido *</label>
                                    <input
                                        type="text"
                                        name="firstLastName"
                                        value={patientFormData.firstLastName}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Segundo Apellido</label>
                                    <input
                                        type="text"
                                        name="secondLastName"
                                        value={patientFormData.secondLastName}
                                        onChange={handlePatientFormInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Sexo *</label>
                                    <select
                                        name="sex"
                                        value={patientFormData.sex}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    >
                                        <option value="masculino">Masculino</option>
                                        <option value="femenino">Femenino</option>
                                        <option value="otro">Otro</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de Nacimiento *</label>
                                    <input
                                        type="date"
                                        name="birthDate"
                                        value={patientFormData.birthDate}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Documento *</label>
                                    <select
                                        name="documentType"
                                        value={patientFormData.documentType}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    >
                                        <option value="cedula">Cédula de Identidad</option>
                                        <option value="pasaporte">Pasaporte</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Nacionalidad del Documento *</label>
                                    <select
                                        name="documentPrefix"
                                        value={patientFormData.documentPrefix}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    >
                                        <option value="V">V (Venezolano)</option>
                                        <option value="E">E (Extranjero)</option>
                                        <option value="P">P (Pasaporte)</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Número de Documento *</label>
                                    <input
                                        type="text"
                                        name="documentNumber"
                                        value={patientFormData.documentNumber}
                                        onChange={handlePatientFormInputChange}
                                        placeholder="18794515"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Teléfono *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={patientFormData.phone}
                                        onChange={handlePatientFormInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={patientFormData.email}
                                        onChange={handlePatientFormInputChange}
                                    />
                                </div>
                            </div>

                            {/* Section: Address */}
                            <h3 style={{ color: '#1d2d48', marginTop: '1.5rem', marginBottom: '1rem' }}>Dirección de Residencia</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Estado</label>
                                    <select
                                        name="state"
                                        value={patientFormData.state}
                                        onChange={handlePatientFormInputChange}
                                    >
                                        <option value="Distrito Capital">Distrito Capital</option>
                                        <option value="Amazonas">Amazonas</option>
                                        <option value="Anzoátegui">Anzoátegui</option>
                                        <option value="Apure">Apure</option>
                                        <option value="Aragua">Aragua</option>
                                        <option value="Barinas">Barinas</option>
                                        <option value="Bolívar">Bolívar</option>
                                        <option value="Carabobo">Carabobo</option>
                                        <option value="Cojedes">Cojedes</option>
                                        <option value="Delta Amacuro">Delta Amacuro</option>
                                        <option value="Falcón">Falcón</option>
                                        <option value="Guárico">Guárico</option>
                                        <option value="Lara">Lara</option>
                                        <option value="Mérida">Mérida</option>
                                        <option value="Miranda">Miranda</option>
                                        <option value="Monagas">Monagas</option>
                                        <option value="Nueva Esparta">Nueva Esparta</option>
                                        <option value="Portuguesa">Portuguesa</option>
                                        <option value="Sucre">Sucre</option>
                                        <option value="Táchira">Táchira</option>
                                        <option value="Trujillo">Trujillo</option>
                                        <option value="Vargas">Vargas</option>
                                        <option value="Yaracuy">Yaracuy</option>
                                        <option value="Zulia">Zulia</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Municipio</label>
                                    <select
                                        name="municipality"
                                        value={patientFormData.municipality}
                                        onChange={handlePatientFormInputChange}
                                        disabled={availableMunicipalities.length === 0}
                                    >
                                        <option value="">Seleccione un municipio</option>
                                        {availableMunicipalities.map((mun) => (
                                            <option key={mun} value={mun}>{mun}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Parroquia</label>
                                    <select
                                        name="parish"
                                        value={patientFormData.parish}
                                        onChange={handlePatientFormInputChange}
                                        disabled={availableParishes.length === 0}
                                    >
                                        <option value="">Seleccione una parroquia</option>
                                        {availableParishes.map((par) => (
                                            <option key={par} value={par}>{par}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Ciudad</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={patientFormData.city}
                                        onChange={handlePatientFormInputChange}
                                        placeholder="Caracas"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Calle/Avenida</label>
                                    <input
                                        type="text"
                                        name="street"
                                        value={patientFormData.street}
                                        onChange={handlePatientFormInputChange}
                                        placeholder="Av. Principal"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Casa/Edificio/Apto</label>
                                    <input
                                        type="text"
                                        name="houseNumber"
                                        value={patientFormData.houseNumber}
                                        onChange={handlePatientFormInputChange}
                                        placeholder="Edificio Torre 1, Apto 5-B"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Dirección Completa (Referencia)</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={patientFormData.address}
                                        onChange={handlePatientFormInputChange}
                                        placeholder="Frente a la plaza, al lado del supermercado"
                                    />
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                marginTop: '1.5rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid #eee'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPatientForm(false);
                                        resetPatientForm();
                                    }}
                                    className="btn btn-secondary"
                                    disabled={loading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Registrando...' : 'Registrar Paciente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MedicalHistory;
